// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { nodeBelongs } from '@cloudscape-design/component-toolkit/dom';
import { useMergeRefs } from '@cloudscape-design/component-toolkit/internal';
import TabTrap from '../tab-trap/index';
import { getFirstFocusable, getLastFocusable } from './utils';
function FocusLock({ className, disabled, autoFocus, restoreFocus, children }, ref) {
    const restoreFocusTargetRef = useRef(null);
    const containerRef = useRef(null);
    const focusFirst = () => {
        var _a;
        if (containerRef.current) {
            (_a = getFirstFocusable(containerRef.current)) === null || _a === void 0 ? void 0 : _a.focus();
        }
    };
    const focusLast = () => {
        var _a;
        if (containerRef.current) {
            (_a = getLastFocusable(containerRef.current)) === null || _a === void 0 ? void 0 : _a.focus();
        }
    };
    // Captures focus when `autoFocus` is set, and the component is mounted or
    // `disabled` changes from true to false.
    useEffect(() => {
        const assignRestoreFocusTarget = () => {
            var _a;
            if (document.activeElement && !((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement))) {
                restoreFocusTargetRef.current = document.activeElement;
            }
        };
        if (autoFocus && !disabled) {
            assignRestoreFocusTarget();
            focusFirst();
        }
    }, [autoFocus, disabled]);
    // Restore focus if `restoreFocus` is set, and `disabled` changes from false to true.
    const [previouslyDisabled, setPreviouslyDisabled] = useState(!!disabled);
    useEffect(() => {
        var _a;
        if (previouslyDisabled !== !!disabled) {
            setPreviouslyDisabled(!!disabled);
            if (restoreFocus && disabled) {
                (_a = restoreFocusTargetRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                restoreFocusTargetRef.current = null;
            }
        }
    }, [previouslyDisabled, disabled, restoreFocus]);
    // Restore focus if `restoreFocus` is set and the component is unmounted.
    // Using a callback ref for this is safer than using useEffect cleanups.
    const restoreFocusHandler = useCallback((elem) => {
        var _a;
        if (elem === null && restoreFocus) {
            (_a = restoreFocusTargetRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            restoreFocusTargetRef.current = null;
        }
    }, [restoreFocus]);
    useImperativeHandle(ref, () => ({ focusFirst }));
    const mergedRef = useMergeRefs(containerRef, restoreFocusHandler);
    const isFocusInside = (event) => nodeBelongs(containerRef.current, event.relatedTarget);
    return (React.createElement(React.Fragment, null,
        React.createElement(TabTrap, { disabled: disabled, focusNextCallback: event => (isFocusInside(event) ? focusLast() : focusFirst()) }),
        React.createElement("div", { className: className, ref: mergedRef }, children),
        React.createElement(TabTrap, { disabled: disabled, focusNextCallback: event => (isFocusInside(event) ? focusFirst() : focusLast()) })));
}
export default React.forwardRef(FocusLock);
//# sourceMappingURL=index.js.map